// Error reporting uses fetch directly to avoid auth dependency

interface ErrorReportData {
  errorMessage: string;
  stackTrace?: string;
  route?: string;
  componentName?: string;
  userAgent?: string;
  severity?: 'low' | 'medium' | 'high' | 'critical';
}

function getDeviceInfo(): string {
  const screenInfo = `${window.screen.width}x${window.screen.height}`;
  const viewportInfo = `${window.innerWidth}x${window.innerHeight}`;
  const pixelRatio = window.devicePixelRatio || 1;
  const online = navigator.onLine ? 'online' : 'offline';
  const platform = navigator.platform || 'unknown';
  const language = navigator.language || 'unknown';
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown';
  
  return `Screen: ${screenInfo} | Viewport: ${viewportInfo} | DPR: ${pixelRatio} | ${online} | Platform: ${platform} | Lang: ${language} | TZ: ${timezone}`;
}

function getTimestamp(): string {
  return new Date().toISOString();
}

function getRecentHistory(): string {
  try {
    const historyLength = window.history.length;
    return `History length: ${historyLength} | Current: ${window.location.href} | Referrer: ${document.referrer || 'none'}`;
  } catch {
    return 'History unavailable';
  }
}

let isReporting = false;
let lastReportTime = 0;
let reportCount = 0;
const RATE_LIMIT_WINDOW_MS = 60000;
const MAX_REPORTS_PER_WINDOW = 10;

export async function reportError(data: ErrorReportData): Promise<boolean> {
  if (isReporting) return false;
  
  const now = Date.now();
  if (now - lastReportTime > RATE_LIMIT_WINDOW_MS) {
    reportCount = 0;
    lastReportTime = now;
  }
  
  if (reportCount >= MAX_REPORTS_PER_WINDOW) {
    console.warn("Error report rate limit reached, skipping");
    return false;
  }
  
  reportCount++;
  
  try {
    isReporting = true;
    
    const contextInfo = [
      `--- Error Context ---`,
      `Timestamp: ${getTimestamp()}`,
      `Device: ${getDeviceInfo()}`,
      `Navigation: ${getRecentHistory()}`,
      `--- Stack Trace ---`,
      data.stackTrace || 'No stack trace available',
    ].join('\n');
    
    const payload: ErrorReportData = {
      errorMessage: data.errorMessage.slice(0, 1000),
      stackTrace: contextInfo.slice(0, 8000),
      route: data.route || window.location.pathname,
      componentName: data.componentName,
      userAgent: navigator.userAgent.slice(0, 500),
      severity: data.severity || 'medium',
    };

    // Use fetch directly instead of apiRequest to avoid auth dependency and potential circular errors
    const response = await fetch("/api/error-reports", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    
    if (!response.ok) {
      console.warn("Error report response not OK:", response.status);
      return false;
    }
    
    return true;
  } catch (error) {
    // Silently fail - we don't want error reporting to cause more errors
    console.warn("Failed to report error:", error);
    return false;
  } finally {
    isReporting = false;
  }
}

export function setupGlobalErrorHandler() {
  window.onerror = (message, source, lineno, colno, error) => {
    reportError({
      errorMessage: String(message),
      stackTrace: error?.stack || `${source}:${lineno}:${colno}`,
      severity: 'high',
    });
  };

  window.onunhandledrejection = (event) => {
    const error = event.reason;
    reportError({
      errorMessage: error?.message || String(error),
      stackTrace: error?.stack,
      severity: 'medium',
    });
  };
}

export function createTimeoutPromise<T>(
  promise: Promise<T>,
  timeoutMs: number,
  timeoutMessage: string = "Operation timed out"
): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs)
    ),
  ]);
}

interface LogErrorOptions {
  severity?: 'low' | 'medium' | 'high' | 'critical';
  componentName?: string;
  route?: string;
  additionalInfo?: string;
}

export const errorService = {
  async logError(error: Error, options: LogErrorOptions = {}): Promise<boolean> {
    const stackTrace = options.additionalInfo 
      ? `${error.stack || ''}\n\nAdditional Info: ${options.additionalInfo}`
      : error.stack;
      
    return reportError({
      errorMessage: error.message,
      stackTrace,
      route: options.route || window.location.pathname,
      componentName: options.componentName,
      severity: options.severity || 'medium',
    });
  },
  
  reportError,
  setupGlobalErrorHandler,
  createTimeoutPromise,
};
